import { createRouter, createWebHashHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import LoginView from "../views/LoginView.vue";
import SignatureManagerView from "../views/SignatureManagerView.vue";
import PlaceholderModuleView from "../views/PlaceholderModuleView.vue";
import { authState, loadCurrentUser } from "@/services/authService";
import DigitalCertificatesView from "@/views/DigitalCertificatesView.vue";
import CertificatesGeneratedView from "@/views/CertificatesGeneratedView.vue";

const placeholder = (meta) => ({
  component: PlaceholderModuleView,
  meta: {
    requiresAuth: true,
    ...meta,
  },
});

const routes = [
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: {
      title: "Login",
      layout: "auth",
      guestOnly: true,
    },
  },
  {
    path: "/painel",
    name: "dashboard",
    ...placeholder({
      title: "Painel",
      eyebrow: "Visão geral",
      status: "Operacional",
      badge: "Novo",
      description:
        "Acompanhe emissões, certificados assinados, uso recente e indicadores do Certificador.",
      roadmap: [
        {
          stage: "01",
          title: "Indicadores",
          description:
            "Total de certificados emitidos, assinados, baixados e falhas recentes.",
        },
        {
          stage: "02",
          title: "Atividade",
          description:
            "Linha do tempo com eventos de emissão, assinatura, validação pública e download.",
        },
        {
          stage: "03",
          title: "Saúde do motor",
          description:
            "Resumo do status do DSS/PAdES, TSA, storage e certificado digital ativo.",
        },
      ],
    }),
  },
  {
    path: "/",
    name: "certificates.emit",
    component: HomeView,
    meta: {
      title: "Emitir certificados",
      eyebrow: "Certificados",
      status: "DSS/PAdES LT ativo",
      requiresAuth: true,
    },
  },
  {
    path: "/certificados",
    name: "certificates.generated",
    component: CertificatesGeneratedView,
    meta: {
      title: "Certificados gerados",
      eyebrow: "Arquivo",
      status: "Histórico",
      requiresAuth: true,
    },
  },
  {
    path: "/assinaturas",
    name: "signatures",
    component: SignatureManagerView,
    meta: {
      title: "Assinaturas visuais",
      eyebrow: "Assinaturas",
      status: "Banco local",
      requiresAuth: true,
    },
  },
  {
    path: "/solicitacoes-assinatura",
    name: "signature.requests",
    ...placeholder({
      title: "Solicitações por link",
      eyebrow: "Assinatura remota",
      badge: "Em breve",
      description:
        "Crie links seguros para coleta de assinatura desenhada, aceite e evidências dos responsáveis.",
      roadmap: [
        {
          stage: "01",
          title: "Link público",
          description:
            "Gerar link único para colaborador, aluno, instrutor ou responsável.",
        },
        {
          stage: "02",
          title: "Assinatura a caneta",
          description:
            "Coletar assinatura visual, dados do assinante, IP, data e consentimento.",
        },
        {
          stage: "03",
          title: "Integração",
          description:
            "Disponibilizar o resultado para Protege, Plataforma X e APIs externas.",
        },
      ],
    }),
  },
  {
    path: "/certificados-digitais",
    name: "digital.certificates",
    component: DigitalCertificatesView,
    meta: {
      title: "Certificados digitais",
      eyebrow: "ICP-Brasil",
      status: "Beta",
      requiresAuth: true,
    },
  },
  {
    path: "/documentos",
    name: "documents",
    ...placeholder({
      title: "Assinar documentos",
      eyebrow: "Documentos",
      badge: "Em breve",
      description:
        "Faça upload de PDFs externos, aplique QR Code, relatório de autenticidade e assinatura digital.",
    }),
  },
  {
    path: "/modelos",
    name: "templates",
    ...placeholder({
      title: "Modelos",
      eyebrow: "Personalização",
      badge: "Em breve",
      description:
        "Crie modelos próprios de certificado, posicione campos, assinaturas e QR Code livremente.",
      roadmap: [
        {
          stage: "01",
          title: "Modelo visual",
          description:
            "Upload de template PDF ou imagem base para emissão personalizada.",
        },
        {
          stage: "02",
          title: "Editor de campos",
          description:
            "Posicionar nome, CPF, treinamento, data, assinaturas e QR Code.",
        },
        {
          stage: "03",
          title: "Reutilização",
          description:
            "Salvar modelos por empresa e aplicar em emissões individuais ou em lote.",
        },
      ],
    }),
  },
  {
    path: "/integracoes",
    name: "integrations",
    ...placeholder({
      title: "APIs e integrações",
      eyebrow: "Ecossistema",
      badge: "Em breve",
      description:
        "Conecte o Certificador ao Protege, Plataforma X, X Flow e outros sistemas da empresa.",
      roadmap: [
        {
          stage: "01",
          title: "Protege",
          description:
            "Solicitar assinatura de retirada de EPI e anexar ao relatório final.",
        },
        {
          stage: "02",
          title: "Plataforma X",
          description:
            "Coletar assinatura de aluno e anexar automaticamente ao certificado do curso.",
        },
        {
          stage: "03",
          title: "X Flow",
          description:
            "Enviar documentos assinados, links de assinatura e notificações automáticas.",
        },
      ],
    }),
  },
  {
    path: "/auditoria",
    name: "audit",
    ...placeholder({
      title: "Auditoria",
      eyebrow: "Rastreabilidade",
      badge: "Em breve",
      description:
        "Consulte eventos de emissão, assinatura, validação pública, download e falhas do motor DSS.",
    }),
  },
  {
    path: "/configuracoes",
    name: "settings",
    ...placeholder({
      title: "Configurações",
      eyebrow: "Administração",
      badge: "Em breve",
      description:
        "Configure empresa, certificado padrão, assinatura padrão, TSA, storage e parâmetros de produção.",
    }),
  },
  {
    path: "/usuarios",
    name: "users",
    ...placeholder({
      title: "Usuários",
      eyebrow: "Equipe",
      badge: "Em breve",
      description:
        "Gerencie usuários, permissões, responsáveis e assinantes autorizados.",
    }),
  },
  {
    path: "/status-dss",
    name: "dss.status",
    ...placeholder({
      title: "Status DSS",
      eyebrow: "Diagnóstico",
      badge: "Local",
      description:
        "Visualize futuramente o resultado do diagnóstico do motor Java DSS/PAdES, TSA e storage.",
      roadmap: [
        {
          stage: "01",
          title: "Java/JAR",
          description:
            "Confirmar se o ambiente possui Java, JAR DSS e permissões corretas.",
        },
        {
          stage: "02",
          title: "TSA e rede",
          description:
            "Testar comunicação com autoridade de carimbo de tempo e endpoints externos.",
        },
        {
          stage: "03",
          title: "Produção",
          description:
            "Exibir alertas de trust store, certificado vencido e falhas recentes.",
        },
      ],
    }),
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (to) => {
  document.title = to.meta?.title
    ? `${to.meta.title} • Certificador`
    : "Certificador";

  if (!authState.initialized) {
    await loadCurrentUser();
  }

  if (to.meta?.guestOnly && authState.user) {
    return { name: "certificates.emit" };
  }

  if (to.meta?.requiresAuth && !authState.user) {
    return {
      name: "login",
      query: {
        redirect: to.fullPath,
      },
    };
  }

  return true;
});

export default router;
